'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

import { stripLocaleFromPath } from '~/i18n/routing'

const THRESHOLDS = [25, 50, 75, 90]

export function useScrollTracking({
	eventNameSuffix,
}: {
	eventNameSuffix: string
}) {
	const reached = useRef(new Set<number>())
	const pathname = stripLocaleFromPath(usePathname() ?? '')

	// Reset when navigating to a new page
	useEffect(() => {
		reached.current = new Set()
	}, [pathname])

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>

		const handleScroll = () => {
			clearTimeout(timer)

			timer = setTimeout(() => {
				const scrollTop = window.scrollY
				const docHeight =
					document.documentElement.scrollHeight - window.innerHeight

				if (docHeight <= 0) return

				const depth = Math.round((scrollTop / docHeight) * 100)

				for (const threshold of THRESHOLDS) {
					if (depth >= threshold && !reached.current.has(threshold)) {
						reached.current.add(threshold)

						window.umami?.track(`scroll-${eventNameSuffix}`, {
							percentage: threshold,
							path: pathname,
							route: pathname,
						})
					}
				}
			}, 100)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [pathname, eventNameSuffix])
}
